import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiShoppingBag, FiUser, FiLock, FiMail, FiArrowRight, FiTrendingUp, FiEye, FiEyeOff, FiLoader, FiPhone, FiMapPin, FiGift, FiCreditCard } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import AuthContext from '../context/AuthContext';
import logo from '../assets/logo.png';

const Register = () => {
  const { login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false); // Nouvel état pour le succès
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [formErrors, setFormErrors] = useState({
    shopName: '',
    username: '',
    email: '',
    password: '',
  });
  const [formData, setFormData] = useState({
    shopName: '',
    username: '',
    phone: '',
    paymentPhone: '',
    address: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');
  const billingCycle = searchParams.get('billingCycle');

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
    setUsernameSuggestions([]); // On efface les suggestions si l'utilisateur modifie le champ
    setFormErrors(prev => ({ ...prev, [id]: '' })); // On efface l'erreur du champ en cours de modification
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormErrors({ shopName: '', username: '', phone: '', address: '', email: '', password: '' }); // Réinitialiser les erreurs
    const { shopName, username, email, password, confirmPassword } = formData;

    if (!shopName || !username || !email || !password || !confirmPassword) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    try {
      // Étape 1: Inscription
      const registerPayload = { ...formData };
      // On n'envoie le plan que s'il est pro ou premium
      if (plan === 'pro' || plan === 'premium') {
        registerPayload.plan = plan;
        if (billingCycle) {
          registerPayload.billingCycle = billingCycle;
        }
      }
      const response = await api.post('/auth/register', registerPayload);
      
      // Au lieu de connecter, on affiche le message de succès
      setRegistrationSuccess(true);
      
      // Sauvegarder la préférence de cycle de facturation pour le paiement ultérieur
      if (billingCycle) {
        localStorage.setItem('preferredBillingCycle', billingCycle);
      }

      toast.success("Compte créé ! Veuillez vérifier votre email.");
      
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error);
      const response = error.response;
      if (response) {
        if (response.status === 400 && response.data.errors) {
          // Erreurs de validation (express-validator)
          const newErrors = {};
          response.data.errors.forEach(err => {
            newErrors[err.path] = err.msg;
          });
          setFormErrors(prev => ({ ...prev, ...newErrors }));
          toast.error("Veuillez corriger les erreurs dans le formulaire.");
        } else if (response.status === 409 && response.data.message) {
          // Conflit (email ou username déjà pris)
          toast.error(response.data.message);
          if (response.data.suggestions && response.data.suggestions.length > 0) {
            setUsernameSuggestions(response.data.suggestions);
          }
        } else {
          toast.error(response.data.message || "Une erreur est survenue. Cet utilisateur ou email existe peut-être déjà.");
        }
      } else {
        toast.error("Une erreur est survenue. Cet utilisateur ou email existe peut-être déjà.");
      }
      setLoading(false); // On arrête le chargement uniquement en cas d'erreur
    } 
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-6">
            <FiMail className="h-10 w-10 text-brand-blue" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Vérifiez votre email</h2>
          <p className="text-gray-600 mb-8 text-lg">
            Un lien de confirmation a été envoyé à <span className="font-bold text-gray-800">{formData.email}</span>.
            <br/>Veuillez cliquer dessus pour activer votre compte.
          </p>
          <Link to="/login" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-blue hover:bg-blue-700 transition-colors">
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Colonne de gauche (Branding) */}
      <div className="hidden lg:flex w-1/2 bg-brand-green items-center justify-center p-12 text-white flex-col relative overflow-hidden font-poppins">
        <FiShoppingBag className="absolute -bottom-24 -right-24 text-white/5 w-96 h-96 transform rotate-12" />
        <FiTrendingUp className="absolute -top-20 -left-24 text-white/5 w-80 h-80 transform rotate-12" />
        <div className="relative z-10 text-center">
          <h1 className="text-4xl font-bold mb-4">Créez votre boutique en ligne</h1>
          <p className="text-lg text-green-100 max-w-md">
            Commencez à transformer la gestion de votre commerce dès aujourd'hui.
          </p>
          <div className="mt-10 border-t border-green-400/30 pt-6">
            <p className="italic text-green-200">"La gestion des dettes était un cauchemar. Maintenant, avec Jaapand-e, tout est plus simple."</p>
            <p className="mt-2 font-semibold">- Moussa Fall, Gérant</p>
          </div>
        </div>
      </div>

      {/* Colonne de droite (Formulaire) */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md space-y-8">
          
          {/* Bannière Offre de Lancement */}
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-4 rounded-xl shadow-lg transform -rotate-1 mb-6 animate-pulse">
            <div className="flex items-center justify-center font-bold text-sm md:text-base">
              <FiGift className="mr-2 text-xl" /> <span>Offre Lancement : Premium GRATUIT (2 mois) pour les 20 premiers !</span>
            </div>
          </div>

          <div className="text-left">
            <Link to="/">
              <img src={logo} alt="Jaapand-e" className="h-12 mb-6" />
            </Link>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-poppins tracking-tight">C'est parti ! 🚀</h2>
            <p className="mt-2 text-gray-500 text-lg">Créez votre compte Jaapand-e en quelques secondes.</p>
          </div>

          <form onSubmit={handleRegister} className="mt-8 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Nom de la boutique</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiShoppingBag size={20} />
                  </div>
                  <input id="shopName" type="text" placeholder="Chez Moussa" value={formData.shopName} onChange={handleChange} className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200 ${formErrors.shopName ? 'border-red-500 focus:border-red-500' : 'border-gray-100 focus:border-brand-blue'}`} />
                </div>
                {formErrors.shopName && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.shopName}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Nom d'utilisateur</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiUser size={20} />
                  </div>
                  <input id="username" type="text" placeholder="moussa.fall" value={formData.username} onChange={handleChange} className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200 ${formErrors.username ? 'border-red-500 focus:border-red-500' : 'border-gray-100 focus:border-brand-blue'}`} />
                </div>
                {formErrors.username && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.username}</p>}
              </div>
            </div>

            {usernameSuggestions.length > 0 && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-sm text-yellow-800 font-medium">Ce nom d'utilisateur est pris. Suggestions :</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {usernameSuggestions.map(suggestion => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, username: suggestion });
                        setUsernameSuggestions([]); // On efface les suggestions après le clic
                      }}
                      className="text-sm bg-yellow-200 text-yellow-900 font-semibold px-3 py-1 rounded-full hover:bg-yellow-300 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Téléphone (WhatsApp)</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiPhone size={20} />
                  </div>
                  <input id="phone" type="tel" placeholder="77 785 49 84" value={formData.phone} onChange={handleChange} className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200 ${formErrors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-100 focus:border-brand-blue'}`} />
                </div>
                {formErrors.phone && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">N° Paiement Wave/OM</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiCreditCard size={20} />
                  </div>
                  <input id="paymentPhone" type="tel" placeholder="Si différent" value={formData.paymentPhone} onChange={handleChange} className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200" />
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-1">
                  Sera utilisé pour recevoir vos paiements Wave ou Orange Money.
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Adresse (Optionnel)</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiMapPin size={20} />
                  </div>
                  <input id="address" type="text" placeholder="Dakar, Sénégal" value={formData.address} onChange={handleChange} className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200 ${formErrors.address ? 'border-red-500 focus:border-red-500' : 'border-gray-100 focus:border-brand-blue'}`} />
                </div>
                {formErrors.address && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.address}</p>}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Email</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiMail size={20} />
                  </div>
                  <input id="email" type="email" placeholder="nodef@gmail.com" value={formData.email} onChange={handleChange} className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200 ${formErrors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-100 focus:border-brand-blue'}`} />
                </div>
                {formErrors.email && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.email}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Mot de passe</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                  <FiLock size={20} />
                </div>
                <input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={formData.password} onChange={handleChange} className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200 ${formErrors.password ? 'border-red-500 focus:border-red-500' : 'border-gray-100 focus:border-brand-blue'}`} />
                <div className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEyeOff className="text-gray-500" /> : <FiEye className="text-gray-500" />}
                </div>
              </div>
              {formErrors.password && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.password}</p>}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Confirmer le mot de passe</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                  <FiLock size={20} />
                </div>
                <input id="confirmPassword" type={showPassword ? "text" : "password"} placeholder="••••••••" value={formData.confirmPassword} onChange={handleChange} className={`w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:bg-white transition-all duration-200 ${formErrors.password ? 'border-red-500 focus:border-red-500' : 'border-gray-100 focus:border-brand-blue'}`} />
              </div>
            </div>

            <div className="pt-2">
              <button 
                className="w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-xl shadow-lg shadow-brand-green/20 text-base font-bold text-white bg-brand-green hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-green transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-75 disabled:cursor-not-allowed" 
                type="submit"
                disabled={loading}
              >
                {loading ? <FiLoader className="animate-spin" /> : <FiArrowRight className="mr-2" />}
                {loading ? 'Finalisation...' : 'Créer mon compte'}
              </button>
            </div>

            <p className="text-center text-sm text-gray-600 mt-6">
              Déjà un compte ? <Link to="/login" className="font-bold text-brand-blue hover:text-blue-700 transition-colors">Se connecter</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;